// Verdichtet Objekt und offene Vorgänge zu dem, was auf der Anrufkarte Platz hat.
// Pure Logik, weil die Entscheidungen (welche Tickets zählen, welche Reihenfolge,
// wie viele passen) sonst nirgends prüfbar wären — Komponententests gibt es in
// diesem Projekt nicht.
//
// „Offen" wird NICHT hier definiert: dafür gibt es istAbgeschlossen in
// lib/tickets/arbeitskoerbe.ts. Eine zweite Definition würde auseinanderlaufen,
// sobald ein Status dazukommt.
import { istAbgeschlossen } from '../tickets/arbeitskoerbe';
// istAbgeschlossen erwartet TicketFuerKorb (u.a. faellig_am als Pflichtfeld),
// liest davon aber ausschließlich `status`. KontextTicket führt faellig_am
// deshalb bewusst nicht — der Typ wird an der einzigen Aufrufstelle unten
// synthetisiert statt das Feld extra durch die ganze Kette zu schleppen.

export interface KontextObjekt {
  id: string;
  bezeichnung: string | null;
  strasse: string | null;
  hausnummer: string | null;
  plz: string | null;
  stadt: string | null;
}

export interface KontextTicket {
  id: string;
  ticket_nummer: string | null;
  titel: string | null;
  status: string;
  prioritaet: string | null;
}

export interface AnrufKontext {
  /** Eine Zeile: Objekt, bei Mietern mit Einheit. Null, wenn nichts bekannt ist. */
  objektZeile: string | null;
  /** Höchstens drei, dringende zuerst. */
  tickets: KontextTicket[];
  /** Wie viele offene Vorgänge darüber hinaus existieren. */
  weitereTickets: number;
  /** Lohnt es, überhaupt Platz für den Zusammenhang zu machen? */
  hatInhalt: boolean;
  /**
   * Mindestens eine Teilabfrage (Objekt/Einheit/Tickets) ist fehlgeschlagen —
   * der Zusammenhang ist unvollständig, nicht bewusst leer. `zuKontext` selbst
   * setzt das nie (reine Verdichtung, kennt keine Netzwerkfehler); der Hook
   * (useAnrufKontext.ts) ergänzt es nachträglich am Ergebnis.
   */
  fehler?: boolean;
}

/** Auf der schmalen Karte ist mehr als drei Zeilen Vorgänge nicht lesbar. */
const MAX_TICKETS = 3;

/** Dringend zuerst — beim Klingeln zählt, was brennt. */
const PRIO_RANG: Record<string, number> = { dringend: 0, hoch: 1, normal: 2, niedrig: 3 };

function objektText(o: KontextObjekt): string {
  const bezeichnung = (o.bezeichnung || '').trim();
  if (bezeichnung) return bezeichnung;
  const strasse = `${(o.strasse || '').trim()} ${(o.hausnummer || '').trim()}`.trim();
  const ort = `${(o.plz || '').trim()} ${(o.stadt || '').trim()}`.trim();
  return [strasse, ort].filter(Boolean).join(', ');
}

export function zuKontext(
  objekt: KontextObjekt | null,
  tickets: KontextTicket[],
  einheit?: string | null,
): AnrufKontext {
  const text = objekt ? objektText(objekt) : '';
  const einheitText = (einheit || '').trim();
  const objektZeile = text
    ? (einheitText ? `${text} · ${einheitText}` : text)
    : null;

  const offen = (tickets || [])
    .filter((t) => !istAbgeschlossen({ status: t.status, faellig_am: null }))
    .sort((a, b) => (PRIO_RANG[a.prioritaet ?? ''] ?? 9) - (PRIO_RANG[b.prioritaet ?? ''] ?? 9));

  return {
    objektZeile,
    tickets: offen.slice(0, MAX_TICKETS),
    weitereTickets: Math.max(0, offen.length - MAX_TICKETS),
    hatInhalt: !!objektZeile || offen.length > 0,
  };
}
